import { ArrowRight, Zap } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1775647862846-f6cffb628c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxiYXNlYmFsbCUyMHBpdGNoZXIlMjBuaWdodCUyMHN0YWRpdW18ZW58MHx8fHwxNzc3NjcwMTEwfDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Pitcher at night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
      </div>

      {/* Decorative grid lines */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
        <div className="h-full w-full bg-[linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] bg-[size:80px_80px]" />
      </div>

      {/* Live indicator */}
      <div className="absolute top-24 right-6 md:right-10 z-10 hidden md:flex items-center gap-2 px-3 py-1.5 border border-[#3a3a3a] bg-black/60 backdrop-blur-sm rounded-sm">
        <span className="w-2 h-2 rounded-full bg-[#eab308] bham-dot" />
        <span className="font-condensed uppercase tracking-[0.2em] text-xs text-neutral-300">
          Now Accepting Athletes
        </span>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-32 w-full">
        <div className="max-w-4xl">
          <div className="bham-rise inline-flex items-center gap-2 mb-6 border-l-2 border-[#eab308] pl-3">
            <Zap size={14} className="text-[#eab308]" />
            <span className="font-condensed uppercase tracking-[0.25em] text-xs text-neutral-300">
              Elite Pitching Development
            </span>
          </div>

          <h1
            className="font-display text-5xl sm:text-7xl lg:text-[8.5rem] leading-[0.85] mb-6 bham-rise"
            style={{ animationDelay: "0.1s" }}
          >
            Making A
            <br />
            Game Your
            <br />
            <span className="text-[#eab308]">Future.</span>
          </h1>

          <p
            className="text-base md:text-xl text-neutral-300 max-w-xl mb-10 bham-rise"
            style={{ animationDelay: "0.25s" }}
          >
            Remote, in-person, and hybrid pitching development for serious high
            school and college athletes. Built by a Professional Baseball Player
            who went from 65 to 95+ mph.
          </p>

          <div
            className="flex flex-col sm:flex-row gap-4 bham-rise"
            style={{ animationDelay: "0.4s" }}
          >
            <button
              data-testid="hero-apply-btn"
              onClick={() => scrollTo("apply")}
              className="group inline-flex items-center justify-center gap-3 bg-[#eab308] hover:bg-[#facc15] text-black font-bold uppercase font-condensed tracking-widest px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5"
            >
              Start Your Transformation
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollTo("services")}
              className="inline-flex items-center justify-center gap-3 border border-[#3a3a3a] hover:border-[#eab308] hover:text-[#eab308] text-white font-bold uppercase font-condensed tracking-widest px-7 py-4 rounded-sm transition-all"
            >
              See Programs
            </button>
          </div>
        </div>

        {/* Hero stats strip */}
        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
          {[
            { v: "65→95+", l: "MPH JUMP" },
            { v: "PRO", l: "ATLANTA BRAVES" },
            { v: "4+", l: "YRS COACHING" },
            { v: "10+", l: "MLB COACHES TRAINED WITH" },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-[#0a0a0a] px-5 py-5 md:py-6"
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display text-2xl md:text-3xl text-[#eab308]">
                {s.v}
              </div>
              <div className="font-condensed uppercase text-[11px] tracking-[0.2em] text-neutral-400 mt-1">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
